/**
 * Kinyarwanda catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Kinyarwanda at a mathematician's register. **Warrant → «Igihamya»**
 * (proof/warrant — flagged). Scientific loanwords are used where Kinyarwanda standardly borrows
 * (logic → Lojiki, modal → Modali, heuristic, scalar, geometry). W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other; the noun uses the
 * umu-/imi- class (umupaka → imipaka). `frameActive` declares one/other plus an explicit `=0`.
 * Several abstract terms are flagged "expert-review suggested." Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const RW_CATALOG = {
    locale: 'rw',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Iperereza',
        [P2.sectionClaims]: 'Ibivugwa',
        [P2.sectionIntegrity]: 'Ubudahemuka',
        [P2.axisWarrantLabel]: 'Igihamya',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Bya empiriki',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Insobanuro} interp {Ubusobanuro} testable {Bishobora kugeragezwa} tested {Byageragejwe} refuted {Byahakanwe} program {Gahunda y’ubushakashatsi} philos {By’ifilozofiya} other {Ntibizwi}}',
        [P2.axisModalLabel]: 'Modali',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Urwego rw’imbaraga {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Bishobora gushakashakwa',
        [P2.stateRefuted]: 'Byahakanwe',
        [P2.stateOutside]: 'Hanze y’urwego',
        [P2.verdictConflict]: 'Bivuguruzanya na {constraints}',
        [P2.verdictOutside]: 'Bisaba {missing}',
        [P2.lossRegisterPlain]: 'Byoroshyejwe mu rurimi rusanzwe — bimwe mu kuri byasibwe.',
        [P2.lossRegisterKid]: 'Byoroshyejwe ku basomyi bato — ibisobanuro byinshi byasibwe.',
        [P2.lossProfileDetail]: 'Umwirondoro wuzuye wa Igihamya / Bya empiriki / Modali wahinnye hano.',
        [P2.lossConflictDetail]: 'Ibisobanuro by’amakimbirane byagabanyijwe muri iyi shusho.',
        [P2.laneVerified]: 'Byemejwe',
        [P2.laneUnverified]: 'Ntibyemejwe',
        [P2.laneConflict]: 'Amakimbirane yagaragajwe',
        [P2.constraintLogic]: 'Lojiki',
        [P2.constraintLightBends]: 'Urumuri rugororoka mu murima w’imbaraga rukurura',
        [P2.constraintParallelPostulate]: 'Postula y’imirongo ihwanye',
        [P2.claimScalarGravity]: 'Inyigisho ya skalari y’imbaraga rukurura',
        [P2.claimNonEuclidean]: 'Jewometiri itari iya Euclide',
        [P2.claimSubstrateSr]: 'Ubusobanuro bwa substrate bw’isano ryihariye',
        [P2.edgeExtObservation]: 'Kwitegereza hanze',
        [P2.edgeResourceSample]: 'Umutungo wo kubara usangiwe',
        [P2.edgeHeuristicSample]: 'Ubuyobozi bwa heuristic',
        [P2.edgeSharedSample]: 'Urufatiro rusangiwe',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Imipaka',
        [P2.sectionEdges]: 'Inkombe',
        [P2.sectionFrame]: 'Ikadiri',
        [P2.sectionExposure]: 'Kugaragazwa',
        [P2.statusAdoptedFormal]: 'Byemejwe (ku mugaragaro)',
        [P2.statusAdoptedInterpretive]: 'Byemejwe (mu busobanuro)',
        [P2.statusSuspended]: 'Byahagaritswe',
        [P2.edgeEffectConstraint]: 'Bigize umupaka (bishobora guhakana)',
        [P2.edgeEffectResource]: 'Umutungo — nta ngaruka ku myanzuro',
        [P2.edgeEffectHeuristic]: 'Heuristic — nta ngaruka ku myanzuro',
        [P2.edgeEffectShared]: 'Urufatiro rusangiwe — nta ngaruka ku myanzuro',
        [P2.frameActive]: '{count, plural, =0 {Nta mipaka ikora} one {# umupaka ukora} other {# imipaka ikora}}',
        [P2.exposureNone]: 'Nta gushingira ku busobanuro',
        [P2.exposureDep]: 'Bishingiye kuri {id}',
        [P2.laneTrackMachine]: 'Imashini',
        [P2.laneTrackExpert]: 'Impuguke',
        [P2.laneTrackCommunity]: 'Umuryango',
        [P2.expertAudited]: 'Byasuzumwe',
        [P2.expertInProgress]: 'Biragenda',
        [P2.expertNotReviewed]: 'Ntibyasubiwemo',
        [P2.communityFlags]: 'Hari ibimenyetso',
        [P2.communityNone]: 'Nta bimenyetso',
        [P2.controlChallenge]: 'Hakana',
        [P2.controlActivate]: 'Tangiza',
        [P2.controlMakeInterpretive]: 'Gira ubusobanuro',
        [P2.controlImportEdge]: 'Injiza inkombe',
        [P2.controlPromote]: 'Zamura',
        [P2.controlUndo]: 'Garura',
        [P2.controlRedo]: 'Ongera ukore',
        [P2.controlRegister]: 'Iyandikishe',
        [P2.promoteRejected]: 'Kuzamurwa byanzwe',
        [P2.promoteAccepted]: 'Kuzamurwa byemewe',
        [P2.promoteReasonRefutationTerminal]: 'Igihamya ntigishobora gukiza guhakanwa.',
        [P2.promoteReasonKeystone]: 'Kuzamura ngombwa bisaba inkunga ya empiriki.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Ubwoko bwa empiriki ntibwashyizweho.',
        [P2.promoteReasonModalNotFixed]: 'Urwego rwa modali ntirwashyizweho.',
        [P2.promoteReasonWarrantNotImproved]: 'Urwego rw’igihamya ntirwateye imbere.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=rw.js.map